/*!
 * SkyPlus Custom Login Component - OAuth/Keycloak Version
 * Brand configuration is read from app.config.json at runtime.
 * To white-label for a new client: edit app.config.json only — no rebuild needed.
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService, RedirectAuthService, AppConfigService } from '@alfresco/adf-core';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';


interface BrandConfig {
  productName:    string;
  companyName:    string;
  tagline:        string;
  logoUrl:        string;
  logoWhiteUrl:   string;
  backgroundUrl:  string;
  primaryColor:   string;
  primaryDark:    string;
  primaryLight:   string;
  accentColor:    string;
}

@Component({
  selector: 'sp-login',
  templateUrl: './skyplus-login.component.html',
  styleUrls: ['./skyplus-login.component.scss']
})
export class SkyPlusLoginComponent implements OnInit, OnDestroy {

  isLoading    = false;
  errorMessage = '';
  displayMode: 'background-image' | 'split-screen' = 'split-screen';

  /** Resolved at runtime from app.config.json → brand section */
  brand: BrandConfig = this.getDefaultBrand();

  private subscriptions: Subscription[] = [];
  private apiUrl: string;
  /** Fallback timer id for a stale/failed OAuth callback (see ngOnInit). */
  private loginTimeout: number | null = null;

  constructor(
    private router:              Router,
    private authService:         AuthenticationService,
    private redirectAuthService: RedirectAuthService,
    private appConfig:           AppConfigService,
    private http:                HttpClient 
  ) {
    this.apiUrl = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    // Login layout is a DEPLOYMENT choice read from app.config.json (a runtime file —
    // editable without rebuilding the app), NOT from the backend brand, so it is never
    // overridden per tenant. Values: 'split-screen' | 'background-image'.
    this.displayMode = this.appConfig.get<'split-screen' | 'background-image'>(
      'skyplus.loginLayout', 'split-screen');
  }

  ngOnInit(): void {
    // Load brand from app.config.json, falling back to SkyPlus defaults
    this.loadBrandConfig();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('code')) {
      // OAuth callback in progress — show the "Redirecting…" state while the
      // auth library redeems the authorization code.
      this.isLoading = true;

      // GUARD against a STALE / already-used code (e.g. a URL copied from
      // another browser or tab, or a back-button replay). That code can never
      // be redeemed, so onLogin never fires and the button would otherwise
      // spin forever — the user had to hand-edit the URL to recover. If a
      // valid session already exists, go straight in; otherwise, if login
      // hasn't completed within a few seconds, strip the leftover params and
      // restore the clickable Sign In button.
      if (this.authService.isLoggedIn()) {
        this.clearOAuthParamsFromUrl();
        this.navigateToDashboard();
        return;
      }
      this.loginTimeout = window.setTimeout(() => {
        if (!this.authService.isLoggedIn()) {
          this.resetToSignIn('Your sign-in session expired. Please sign in again.');
        }
      }, 8000);
    } else {
      this.checkAndRedirect();
    }

    const loginSub = this.authService.onLogin.subscribe(() => {
      this.clearLoginTimeout();
      this.navigateToDashboard();
    });
    this.subscriptions.push(loginSub);
  }

  ngOnDestroy(): void {
    this.clearLoginTimeout();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /** Cancel the stale-code fallback timer (login succeeded or page left). */
  private clearLoginTimeout(): void {
    if (this.loginTimeout !== null) {
      clearTimeout(this.loginTimeout);
      this.loginTimeout = null;
    }
  }

  /** Remove OAuth callback params from the URL without reloading, so a stale
   *  code can't keep re-triggering the redirecting state on refresh. */
  private clearOAuthParamsFromUrl(): void {
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch { /* history not available — non-fatal */ }
  }

  /** Recover from a stale/failed callback: clean the URL and re-enable Sign In. */
  private resetToSignIn(message: string): void {
    this.clearLoginTimeout();
    this.clearOAuthParamsFromUrl();
    this.isLoading = false;
    this.errorMessage = message || '';
  }

  // ── Brand loading ─────────────────────────────────────────────────────────

  private loadBrandConfig(): void {
  // First, try the public tenant brand endpoint - the authoritative source.
  // If it succeeds, those values win. If it fails (network down, endpoint
  // not yet deployed), we silently fall back to app.config.json.
  this.http.get<Partial<BrandConfig>>(`${this.apiUrl}/tenant/brand`)
    .subscribe({
      next: (apiBrand) => {
        this.mergeBrand(apiBrand);
        this.applyBrandColors();
      },
      error: () => {
        // Fallback: read brand from app.config.json (existing behaviour)
        this.loadBrandFromAppConfig();
        this.applyBrandColors();
      }
    });
}

private loadBrandFromAppConfig(): void {
  try {
    const cfg = this.appConfig.get<Partial<BrandConfig>>('brand', {});
    this.mergeBrand(cfg);
  } catch (e) {
    this.brand = this.getDefaultBrand();
  }
}

private mergeBrand(src: Partial<BrandConfig> | null | undefined): void {
  const d = this.getDefaultBrand();
  this.brand = {
    productName:   (src as any)?.productName || d.productName,
    companyName:   src?.companyName   || d.companyName,
    tagline:       src?.tagline       || d.tagline,
    logoUrl:       src?.logoUrl       || d.logoUrl,
    logoWhiteUrl:  src?.logoWhiteUrl  || d.logoWhiteUrl,
    backgroundUrl: (src as any)?.backgroundUrl || d.backgroundUrl,
    primaryColor:  src?.primaryColor  || d.primaryColor,
    primaryDark:   src?.primaryDark   || d.primaryDark,
    primaryLight:  src?.primaryLight  || d.primaryLight,
    accentColor:   src?.accentColor   || d.accentColor,
  };
}

  /**
   * Injects brand colours as CSS custom properties on document.documentElement.
   * The SCSS uses var(--brand-primary) etc. so colours change without a rebuild.
   */
  private applyBrandColors(): void {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary',       this.brand.primaryColor);
    root.style.setProperty('--brand-primary-dark',  this.brand.primaryDark);
    root.style.setProperty('--brand-primary-light', this.brand.primaryLight);
    root.style.setProperty('--brand-accent',        this.brand.accentColor);
    // Brand the browser tab on the login screen too (the main app sets this
    // post-login via SectorService.applyBrand(); pre-login it stayed static).
    const title = this.brand.productName || this.brand.companyName;
    if (title) { document.title = title; }
  }

  private getDefaultBrand(): BrandConfig {
    // NEUTRAL fallback ONLY. The real brand comes from GET /api/tenant/brand
    // (the active profile / site application.yml). Never hardcode a client here.
    return {
      productName:   'Document Management',
      companyName:   'Document Management',
      tagline:       '',
      logoUrl:       'assets/images/skyplus/logo.png',
      logoWhiteUrl:  'assets/images/skyplus/logo-white.png',
      backgroundUrl: 'assets/images/skyplus/login-background.jpg',
      primaryColor:  '#2c3e50',
      primaryDark:   '#1a252f',
      primaryLight:  '#3d566e',
      accentColor:   '#2980b9',
    };
  }

  // ── Auth flow ─────────────────────────────────────────────────────────────

  private checkAndRedirect(): void {
    this.isLoading = false;
    setTimeout(() => {
      if (this.authService.isLoggedIn()) {
        this.navigateToDashboard();
      }
    }, 300);
  }

  private navigateToDashboard(): void {
    this.router.navigate(['/dashboard'], { replaceUrl: true, queryParams: {} });
  }

  toggleDisplayMode(): void {
    this.displayMode = this.displayMode === 'background-image' ? 'split-screen' : 'background-image';
  }

  onLoginClick(): void {
    this.clearLoginTimeout();
    this.isLoading    = true;
    this.errorMessage = '';
    try {
      this.redirectAuthService.login();
    } catch (error: any) {
      this.errorMessage = 'Failed to initiate login. Please try again.';
      this.isLoading = false;
    }
  }
}